import { lazy } from "react";
import { ArrowDownToLine, Boxes, Copy, Flag, HeartCrack, KeyRound, LocateFixed, Merge, SearchX, Split, Tally5, Unlink } from "lucide-react";
import { defineWebModule } from "../../web/module";
import { animalRef, herdOf, isCurrent } from "./web/common";
import "./web/herd.css";

// Pages load when opened, so the app starts fast on a phone with weak signal.
const HerdSection = lazy(() => import("./web/AnimalHerd").then((m) => ({ default: m.HerdSection })));
const LotSection = lazy(() => import("./web/Lots").then((m) => ({ default: m.LotSection })));
const PlaceCountsSection = lazy(() => import("./web/Lots").then((m) => ({ default: m.PlaceCountsSection })));
const SavedListSection = lazy(() => import("./web/Lists").then((m) => ({ default: m.SavedListSection })));
const CountForm = lazy(() => import("./web/forms").then((m) => ({ default: m.CountForm })));
const DeathForm = lazy(() => import("./web/forms").then((m) => ({ default: m.DeathForm })));
const HerdWorkspace = lazy(() => import("./web/Workspace").then((m) => ({ default: m.HerdWorkspace })));
const IdentifierForm = lazy(() => import("./web/forms").then((m) => ({ default: m.IdentifierForm })));
const LifecycleForm = lazy(() => import("./web/forms").then((m) => ({ default: m.LifecycleForm })));
const LotChangeForm = lazy(() => import("./web/forms").then((m) => ({ default: m.LotChangeForm })));
const OwnershipForm = lazy(() => import("./web/forms").then((m) => ({ default: m.OwnershipForm })));
const TradeDetail = lazy(() => import("./web/forms").then((m) => ({ default: m.TradeDetail })));

export default defineWebModule({
  id: "herd",
  icons: {
    arrive: ArrowDownToLine, death: HeartCrack, cull: Flag, missing: SearchX, found: LocateFixed, wean: Unlink, split: Split, owner: KeyRound,
    count: Tally5, lot: Boxes, copy: Copy, merge: Merge,
  },
  events: {
    "herd.arrival": { Form: LifecycleForm, Detail: TradeDetail },
    "herd.departure": { Form: LifecycleForm, Detail: TradeDetail },
    "herd.death": { Form: DeathForm },
    "herd.identifier": { Form: IdentifierForm },
    "herd.count": { Form: CountForm },
    "herd.ownership": { Form: OwnershipForm },
    "herd.lot": { Form: LotChangeForm },
  },
  animalSections: [{ id: "herd", label: "Herd status", order: 10, show: (a) => !!herdOf(a), Component: HerdSection }],
  animalActions: [
    { id: "herd-found", label: "Mark found", icon: "found", eventType: "herd.found", when: (a) => a.status === "missing", priority: 95 },
    { id: "herd-sale", label: "Record sale or shipment", icon: "truck", eventType: "herd.departure", when: isCurrent, priority: 40,
      data: (a) => (herdOf(a)?.on_cull_list ? { kind: "sold", reason: "cull" } : { kind: "sold" }) },
    { id: "herd-retag", label: "Retag", icon: "tag", eventType: "herd.identifier", when: isCurrent, priority: 35,
      data: (a) => ({ action: "retag", scheme: "visual", ...(a.tag ? { old_value: a.tag } : {}) }) },
    { id: "herd-death", label: "Record death", icon: "death", eventType: "herd.death", when: isCurrent, priority: 30, subjects: (a) => [animalRef(a)] },
    { id: "herd-cull", label: "Cull", icon: "cull", eventType: "herd.cull", when: (a) => isCurrent(a) && !herdOf(a)?.on_cull_list, priority: 25 },
    { id: "herd-keep", label: "Take off the cull list", icon: "cull", eventType: "herd.cull", when: (a) => isCurrent(a) && !!herdOf(a)?.on_cull_list, priority: 24, data: () => ({ decision: "keep" }) },
    { id: "herd-missing", label: "Mark missing", icon: "missing", eventType: "herd.missing", when: (a) => a.status === "active", priority: 20 },
  ],
  workspaces: { herd: HerdWorkspace },
  groupSections: [
    { id: "herd-lot", label: "Lot", order: 10, show: (g) => g.kind === "lot", Component: LotSection },
    { id: "herd-list", label: "Live list", order: 10, show: (g) => g.kind === "saved_view", Component: SavedListSection },
  ],
  placeSections: [{ id: "herd-counts", label: "Head counts", order: 20, show: (p) => !!p.facets?.herd, Component: PlaceCountsSection }],
});
